import { useState, useEffect, useRef } from 'react';
import {
  CheckSquare,
  Square,
  X,
  Plus,
  MessageCircle,
  MessageSquare,
  MessagesSquare,
  HelpCircle,
  Headphones,
  Bot,
  Sparkles,
  Send,
  Mail,
  Phone,
  LucideIcon,
  Type,
  Layout,
  Palette,
  MessageSquare as MessageSquareIcon,
  Shield,
  Eye,
} from 'lucide-react';
import { Button } from '@aaas/shared';
import { SpaceSelector } from './SpaceSelector';
import type { Widget, WidgetFormData } from '../types/widget.types';

interface WidgetFormProps {
  initialData?: Widget;
  onSubmit: (data: WidgetFormData) => Promise<void>;
  onCancel: () => void;
  submitLabel: string;
  isSubmitting?: boolean;
}

const DEFAULT_VALUES: WidgetFormData = {
  name: '',
  description: '',
  space_ids: [],
  theme_color: '#3B82F6',
  accent_color: '#10B981',
  text_color: '#1F2937',
  button_color: '#3B82F6',
  button_icon_color: '#FFFFFF',
  button_icon: 'message-circle',
  button_text: 'Chat',
  button_border_radius: '50%',
  position: 'bottom-right',
  widget_size: 'normal',
  widget_height: '550px',
  popup_border_radius: '12px',
  header_title: 'AI Assistant',
  bot_name: 'Assistant',
  avatar_icon: '🤖',
  show_powered_by: true,
  welcome_message: 'How can I help you today?',
  input_placeholder: 'Type your question...',
  send_button_text: 'Send',
  suggested_questions: [],
  auto_open: false,
  auto_open_delay: 3000,
  persist_conversation: true,
  max_message_length: 2000,
  timeout: 30000,
  allowed_domains: [],
  rate_limit_per_hour: 100,
  rate_limit_per_minute: 10,
};

const AVATAR_OPTIONS = ['🤖', '💬', '🧠', '✨', '🎯', '💡', '🔮', '🌟', '👋', '🎪'];

const BUTTON_ICON_OPTIONS: { value: string; label: string; icon: LucideIcon }[] = [
  { value: 'message-circle', label: 'Message', icon: MessageCircle },
  { value: 'message-square', label: 'Chat', icon: MessageSquare },
  { value: 'messages-square', label: 'Messages', icon: MessagesSquare },
  { value: 'help-circle', label: 'Help', icon: HelpCircle },
  { value: 'headphones', label: 'Support', icon: Headphones },
  { value: 'bot', label: 'Bot', icon: Bot },
  { value: 'sparkles', label: 'AI', icon: Sparkles },
  { value: 'send', label: 'Send', icon: Send },
  { value: 'mail', label: 'Mail', icon: Mail },
  { value: 'phone', label: 'Phone', icon: Phone },
];

const POSITION_OPTIONS = [
  { value: 'bottom-right', label: 'Bottom Right' },
  { value: 'bottom-left', label: 'Bottom Left' },
  { value: 'top-right', label: 'Top Right' },
  { value: 'top-left', label: 'Top Left' },
];

type TabType =
  | 'basic'
  | 'branding'
  | 'layout'
  | 'appearance'
  | 'messaging'
  | 'behavior'
  | 'security';

const TABS: { id: TabType; label: string; icon: LucideIcon }[] = [
  { id: 'basic', label: 'Basic Info', icon: Type },
  { id: 'branding', label: 'Branding', icon: Type },
  { id: 'layout', label: 'Layout', icon: Layout },
  { id: 'appearance', label: 'Appearance', icon: Palette },
  { id: 'messaging', label: 'Messaging', icon: MessageSquareIcon },
  { id: 'behavior', label: 'Behavior', icon: Bot },
  { id: 'security', label: 'Security', icon: Shield },
];

export function WidgetForm({
  initialData,
  onSubmit,
  onCancel,
  submitLabel,
  isSubmitting = false,
}: WidgetFormProps) {
  const [formData, setFormData] = useState<WidgetFormData>(() => {
    if (initialData) {
      return {
        name: initialData.name,
        description: initialData.description || '',
        space_ids: initialData.spaces?.map((s) => s.id) ?? [],
        theme_color: initialData.theme_color,
        accent_color: initialData.accent_color,
        text_color: initialData.text_color,
        button_color: initialData.button_color,
        button_icon_color: initialData.button_icon_color,
        button_icon: initialData.button_icon,
        button_text: initialData.button_text,
        button_border_radius: initialData.button_border_radius,
        position: initialData.position,
        widget_size: initialData.widget_size,
        widget_height: initialData.widget_height,
        popup_border_radius: initialData.popup_border_radius,
        header_title: initialData.header_title,
        bot_name: initialData.bot_name,
        avatar_icon: initialData.avatar_icon,
        show_powered_by: initialData.show_powered_by,
        welcome_message: initialData.welcome_message,
        input_placeholder: initialData.input_placeholder,
        send_button_text: initialData.send_button_text,
        suggested_questions: initialData.suggested_questions || [],
        auto_open: initialData.auto_open,
        auto_open_delay: initialData.auto_open_delay,
        persist_conversation: initialData.persist_conversation,
        max_message_length: initialData.max_message_length,
        timeout: initialData.timeout,
        allowed_domains: initialData.allowed_domains || [],
        rate_limit_per_hour: initialData.rate_limit_per_hour,
        rate_limit_per_minute: initialData.rate_limit_per_minute,
      };
    }
    return DEFAULT_VALUES;
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = useState<TabType>('basic');
  const [newQuestion, setNewQuestion] = useState('');
  const previewRef = useRef<HTMLIFrameElement>(null);
  const [previewKey, setPreviewKey] = useState(0);
  const [viewMode, setViewMode] = useState<'form' | 'preview'>('form');

  const handleInputChange = <K extends keyof WidgetFormData>(
    field: K,
    value: WidgetFormData[K]
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const addSuggestedQuestion = () => {
    if (newQuestion.trim() && formData.suggested_questions.length < 4) {
      handleInputChange('suggested_questions', [
        ...formData.suggested_questions,
        newQuestion.trim(),
      ]);
      setNewQuestion('');
    }
  };

  const removeSuggestedQuestion = (index: number) => {
    handleInputChange(
      'suggested_questions',
      formData.suggested_questions.filter((_, i) => i !== index)
    );
  };

  const handleArrayInputChange = (field: 'allowed_domains', value: string) => {
    const items = value.split('\n').filter((item) => item.trim());
    handleInputChange(field, items);
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Widget name is required';
    }

    if (formData.space_ids.length === 0) {
      newErrors.space_ids = 'Select at least 1 space';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) {
      // Switch to the tab with the first error
      if (errors.name || errors.space_ids) {
        setActiveTab('basic');
      }
      return;
    }
    await onSubmit(formData);
  };

  // Generate the preview HTML with embedded widget
  const generatePreviewHtml = () => {
    const isDev = typeof window !== 'undefined' && window.location.hostname === 'localhost';
    const apiOrigin = isDev ? 'http://localhost:8100' : window.location.origin;
    const suggestedQuestionsJson = JSON.stringify(formData.suggested_questions);

    return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Widget Preview</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%);
      min-height: 100vh;
      position: relative;
      overflow: hidden;
    }
    .shape-container {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      overflow: hidden;
    }
    .circle {
      position: absolute;
      border-radius: 50%;
      opacity: 0.4;
    }
    .circle-1 {
      width: 200px;
      height: 200px;
      background: ${formData.theme_color}30;
      top: -50px;
      right: -50px;
    }
    .circle-2 {
      width: 150px;
      height: 150px;
      background: ${formData.theme_color}20;
      bottom: 100px;
      left: 50px;
    }
    .circle-3 {
      width: 100px;
      height: 100px;
      background: ${formData.theme_color}15;
      top: 40%;
      left: 60%;
    }
    .box {
      position: absolute;
      background: white;
      border-radius: 12px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.06);
    }
    .box-1 {
      width: 280px;
      height: 120px;
      top: 80px;
      left: 40px;
    }
    .box-2 {
      width: 220px;
      height: 140px;
      top: 220px;
      right: 60px;
    }
    .box-3 {
      width: 180px;
      height: 100px;
      bottom: 180px;
      left: 80px;
    }
    .box-4 {
      width: 160px;
      height: 80px;
      top: 50%;
      left: 35%;
    }
    .line {
      position: absolute;
      background: ${formData.theme_color}20;
    }
    .line-1 {
      width: 120px;
      height: 4px;
      top: 140px;
      left: 80px;
      border-radius: 2px;
    }
    .line-2 {
      width: 90px;
      height: 4px;
      top: 280px;
      right: 100px;
      border-radius: 2px;
    }
    .dot-grid {
      position: absolute;
      top: 60%;
      right: 30%;
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 12px;
    }
    .dot {
      width: 8px;
      height: 8px;
      background: ${formData.theme_color}30;
      border-radius: 50%;
    }
  </style>
</head>
<body>
  <div class="shape-container">
    <div class="circle circle-1"></div>
    <div class="circle circle-2"></div>
    <div class="circle circle-3"></div>

    <div class="box box-1"></div>
    <div class="box box-2"></div>
    <div class="box box-3"></div>
    <div class="box box-4"></div>

    <div class="line line-1"></div>
    <div class="line line-2"></div>

    <div class="dot-grid">
      <div class="dot"></div>
      <div class="dot"></div>
      <div class="dot"></div>
      <div class="dot"></div>
      <div class="dot"></div>
      <div class="dot"></div>
      <div class="dot"></div>
      <div class="dot"></div>
    </div>
  </div>
  <!-- KMS Chat Widget -->
  <script type="text/javascript">
    window.$kmsWidget = [];
    window.KMS_WIDGET_CONFIG = {
      botId: 'preview',
      apiOrigin: '${apiOrigin}',
      widgetOrigin: '${apiOrigin}',
      position: '${formData.position}',
      themeColor: '${formData.theme_color}',
      accentColor: '${formData.accent_color}',
      textColor: '${formData.text_color}',
      buttonColor: '${formData.button_color}',
      buttonIconColor: '${formData.button_icon_color}',
      buttonIcon: '${formData.button_icon}',
      buttonBorderRadius: '${formData.button_border_radius}',
      welcomeMessage: '${formData.welcome_message.replace(/'/g, "\\'")}',
      buttonText: '${formData.button_text.replace(/'/g, "\\'")}',
      autoOpen: ${formData.auto_open},
      autoOpenDelay: ${formData.auto_open_delay},
      preview: true,
      headerTitle: '${formData.header_title.replace(/'/g, "\\'")}',
      botName: '${formData.bot_name.replace(/'/g, "\\'")}',
      avatarIcon: '${formData.avatar_icon}',
      showPoweredBy: ${formData.show_powered_by},
      widgetSize: '${formData.widget_size}',
      widgetHeight: '${formData.widget_height}',
      popupBorderRadius: '${formData.popup_border_radius}',
      inputPlaceholder: '${formData.input_placeholder.replace(/'/g, "\\'")}',
      sendButtonText: '${formData.send_button_text.replace(/'/g, "\\'")}',
      suggestedQuestions: ${suggestedQuestionsJson}
    };
    (function() {
      var d = document;
      var s = d.createElement("script");
      s.src = "${apiOrigin}/widget.js?t=" + Date.now();
      s.async = 1;
      d.getElementsByTagName("head")[0].appendChild(s);
    })();
  </script>
</body>
</html>`;
  };

  // Reload preview when settings change (with debounce)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPreviewKey((prev) => prev + 1);
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [
    formData.theme_color,
    formData.accent_color,
    formData.text_color,
    formData.button_color,
    formData.button_icon_color,
    formData.button_icon,
    formData.button_border_radius,
    formData.welcome_message,
    formData.button_text,
    formData.auto_open,
    formData.header_title,
    formData.bot_name,
    formData.avatar_icon,
    formData.show_powered_by,
    formData.position,
    formData.widget_size,
    formData.widget_height,
    formData.popup_border_radius,
    formData.input_placeholder,
    formData.send_button_text,
    formData.suggested_questions,
  ]);

  const renderBasicTab = () => (
    <div className="space-y-6">
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Widget Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          maxLength={255}
          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            errors.name ? 'border-red-500' : ''
          }`}
          placeholder="e.g., Customer Support Widget"
        />
        <div className="flex justify-between text-xs text-gray-500">
          <span>{errors.name || 'A unique name for this widget'}</span>
          <span className={errors.name ? 'text-red-500' : ''}>{formData.name.length}/255</span>
        </div>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Description</label>
        <textarea
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          maxLength={1000}
          rows={3}
          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Describe what this widget is for..."
        />
        <div className="flex justify-between text-xs text-gray-500">
          <span>Optional description for your reference</span>
          <span>{formData.description.length}/1000</span>
        </div>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Associated Spaces <span className="text-red-500">*</span>
        </label>
        <SpaceSelector
          selectedSpaceIds={formData.space_ids}
          onChange={(ids) => handleInputChange('space_ids', ids)}
          error={errors.space_ids}
        />
        <p className="text-xs text-gray-500">
          Select which knowledge spaces this widget can access
        </p>
      </div>
    </div>
  );

  const renderBrandingTab = () => (
    <div className="space-y-6">
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Header Title</label>
        <input
          type="text"
          value={formData.header_title}
          onChange={(e) => handleInputChange('header_title', e.target.value)}
          maxLength={30}
          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="AI Assistant"
        />
        <div className="flex justify-between text-xs text-gray-500">
          <span>Title displayed in the chat header</span>
          <span>{formData.header_title.length}/30</span>
        </div>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Bot Name</label>
        <input
          type="text"
          value={formData.bot_name}
          onChange={(e) => handleInputChange('bot_name', e.target.value)}
          maxLength={20}
          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Assistant"
        />
        <div className="flex justify-between text-xs text-gray-500">
          <span>Name shown next to bot messages</span>
          <span>{formData.bot_name.length}/20</span>
        </div>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Avatar Icon</label>
        <div className="flex flex-wrap gap-2">
          {AVATAR_OPTIONS.map((icon) => (
            <button
              key={icon}
              type="button"
              onClick={() => handleInputChange('avatar_icon', icon)}
              className={`w-12 h-12 p-2 text-xl rounded-xl border-2 transition-all duration-200 flex items-center justify-center ${
                formData.avatar_icon === icon
                  ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-indigo-50 ring-2 ring-blue-100 scale-110 shadow-lg shadow-blue-100'
                  : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50 hover:scale-105'
              }`}
            >
              {icon}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-500">Icon displayed in the chat header</p>
      </div>

      <div className="space-y-2">
        <button
          type="button"
          onClick={() => handleInputChange('show_powered_by', !formData.show_powered_by)}
          className="flex items-center gap-2 text-sm text-gray-700 hover:text-gray-900 transition-colors"
        >
          {formData.show_powered_by ? (
            <CheckSquare className="w-5 h-5 text-blue-600" />
          ) : (
            <Square className="w-5 h-5 text-gray-400" />
          )}
          <span>Show "Powered by" badge</span>
        </button>
      </div>
    </div>
  );

  const renderLayoutTab = () => (
    <div className="space-y-6">
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Widget Position</label>
        <div className="grid grid-cols-2 gap-2">
          {POSITION_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleInputChange('position', option.value)}
              className={`px-4 py-2 text-sm rounded-lg border-2 transition-all ${
                formData.position === option.value
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Widget Size</label>
        <select
          value={formData.widget_size}
          onChange={(e) => handleInputChange('widget_size', e.target.value)}
          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="compact">Compact</option>
          <option value="normal">Normal</option>
          <option value="large">Large</option>
        </select>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Widget Height</label>
        <input
          type="text"
          value={formData.widget_height}
          onChange={(e) => handleInputChange('widget_height', e.target.value)}
          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="550px"
        />
        <p className="text-xs text-gray-500">Height in pixels (e.g., 550px, 600px)</p>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Popup Border Radius</label>
        <input
          type="text"
          value={formData.popup_border_radius}
          onChange={(e) => handleInputChange('popup_border_radius', e.target.value)}
          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="12px"
        />
        <p className="text-xs text-gray-500">Corner rounding (e.g., 8px, 12px, 16px)</p>
      </div>
    </div>
  );

  const renderAppearanceTab = () => (
    <div className="space-y-6">
      {['theme_color', 'accent_color', 'text_color', 'button_color', 'button_icon_color'].map(
        (key) => (
          <div key={key} className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 capitalize">
              {key.replace(/_/g, ' ')}
            </label>
            <div className="flex gap-2">
              <input
                type="color"
                value={formData[key as keyof WidgetFormData] as string}
                onChange={(e) => handleInputChange(key as keyof WidgetFormData, e.target.value)}
                className="h-10 w-20 border rounded-lg cursor-pointer"
              />
              <input
                type="text"
                value={formData[key as keyof WidgetFormData] as string}
                onChange={(e) => handleInputChange(key as keyof WidgetFormData, e.target.value)}
                className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                placeholder="#3B82F6"
              />
            </div>
          </div>
        )
      )}

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Button Icon</label>
        <div className="flex flex-wrap gap-2">
          {BUTTON_ICON_OPTIONS.map((option) => {
            const IconComponent = option.icon;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => handleInputChange('button_icon', option.value)}
                className={`w-12 h-12 p-2 rounded-xl border-2 transition-all duration-200 flex items-center justify-center ${
                  formData.button_icon === option.value
                    ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-indigo-50 ring-2 ring-blue-100 scale-110 shadow-lg shadow-blue-100'
                    : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50 hover:scale-105'
                }`}
                title={option.label}
              >
                <IconComponent className="w-5 h-5 text-gray-700" />
              </button>
            );
          })}
        </div>
        <p className="text-xs text-gray-500">Icon displayed on the floating chat button</p>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Button Border Radius</label>
        <input
          type="text"
          value={formData.button_border_radius}
          onChange={(e) => handleInputChange('button_border_radius', e.target.value)}
          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="50%"
        />
        <p className="text-xs text-gray-500">50% for circle, or pixel value (e.g., 8px, 16px)</p>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Button Tooltip</label>
        <input
          type="text"
          value={formData.button_text}
          onChange={(e) => handleInputChange('button_text', e.target.value)}
          maxLength={20}
          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Chat"
        />
        <div className="flex justify-between text-xs text-gray-500">
          <span>Tooltip shown when hovering over the button</span>
          <span>{formData.button_text.length}/20</span>
        </div>
      </div>
    </div>
  );

  const renderMessagingTab = () => (
    <div className="space-y-6">
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Welcome Message</label>
        <input
          type="text"
          value={formData.welcome_message}
          onChange={(e) => handleInputChange('welcome_message', e.target.value)}
          maxLength={100}
          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="How can I help you today?"
        />
        <div className="flex justify-between text-xs text-gray-500">
          <span>Initial greeting message shown to users</span>
          <span>{formData.welcome_message.length}/100</span>
        </div>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Input Placeholder</label>
        <input
          type="text"
          value={formData.input_placeholder}
          onChange={(e) => handleInputChange('input_placeholder', e.target.value)}
          maxLength={50}
          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Type your question..."
        />
        <div className="flex justify-between text-xs text-gray-500">
          <span>Placeholder text in the message input</span>
          <span>{formData.input_placeholder.length}/50</span>
        </div>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Send Button Text</label>
        <input
          type="text"
          value={formData.send_button_text}
          onChange={(e) => handleInputChange('send_button_text', e.target.value)}
          maxLength={15}
          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Send"
        />
        <div className="flex justify-between text-xs text-gray-500">
          <span>Text displayed on the send button</span>
          <span>{formData.send_button_text.length}/15</span>
        </div>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Suggested Questions</label>
        <div className="space-y-2">
          {formData.suggested_questions.map((question, index) => (
            <div key={index} className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg">
              <span className="flex-1 text-sm text-gray-700">{question}</span>
              <button
                type="button"
                onClick={() => removeSuggestedQuestion(index)}
                className="text-gray-400 hover:text-red-500 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
          {formData.suggested_questions.length < 4 && (
            <div className="flex gap-2">
              <input
                type="text"
                value={newQuestion}
                onChange={(e) => setNewQuestion(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addSuggestedQuestion())}
                className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="Add a suggested question..."
                maxLength={100}
              />
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={addSuggestedQuestion}
                disabled={!newQuestion.trim()}
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
        <p className="text-xs text-gray-500">Quick questions users can click to ask (max 4)</p>
      </div>
    </div>
  );

  const renderBehaviorTab = () => (
    <div className="space-y-6">
      <div className="space-y-2">
        <button
          type="button"
          onClick={() => handleInputChange('auto_open', !formData.auto_open)}
          className="flex items-center gap-2 text-sm text-gray-700 hover:text-gray-900 transition-colors"
        >
          {formData.auto_open ? (
            <CheckSquare className="w-5 h-5 text-blue-600" />
          ) : (
            <Square className="w-5 h-5 text-gray-400" />
          )}
          <span>Automatically open the widget when the page loads</span>
        </button>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Auto-open Delay (ms)</label>
        <input
          type="number"
          value={formData.auto_open_delay}
          onChange={(e) => handleInputChange('auto_open_delay', parseInt(e.target.value) || 0)}
          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          min="0"
        />
        <p className="text-xs text-gray-500">Delay before auto-opening (in milliseconds)</p>
      </div>

      <div className="space-y-2">
        <button
          type="button"
          onClick={() => handleInputChange('persist_conversation', !formData.persist_conversation)}
          className="flex items-center gap-2 text-sm text-gray-700 hover:text-gray-900 transition-colors"
        >
          {formData.persist_conversation ? (
            <CheckSquare className="w-5 h-5 text-blue-600" />
          ) : (
            <Square className="w-5 h-5 text-gray-400" />
          )}
          <span>Persist conversation between page visits</span>
        </button>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Max Message Length</label>
        <input
          type="number"
          value={formData.max_message_length}
          onChange={(e) => handleInputChange('max_message_length', parseInt(e.target.value) || 0)}
          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          min="1"
        />
        <p className="text-xs text-gray-500">Maximum characters allowed in a message</p>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Timeout (ms)</label>
        <input
          type="number"
          value={formData.timeout}
          onChange={(e) => handleInputChange('timeout', parseInt(e.target.value) || 0)}
          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          min="1000"
        />
        <p className="text-xs text-gray-500">Request timeout in milliseconds</p>
      </div>
    </div>
  );

  const renderSecurityTab = () => (
    <div className="space-y-6">
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Allowed Domains</label>
        <textarea
          value={formData.allowed_domains.join('\n')}
          onChange={(e) => handleArrayInputChange('allowed_domains', e.target.value)}
          placeholder={'example.com\nsubdomain.example.com\n*.example.org'}
          rows={6}
          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
        />
        <p className="text-xs text-gray-500">
          Enter one domain per line. Leave empty to allow all domains. Supports wildcards (e.g.,
          *.example.com)
        </p>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Rate Limit (requests per hour)
        </label>
        <input
          type="number"
          value={formData.rate_limit_per_hour}
          onChange={(e) => handleInputChange('rate_limit_per_hour', parseInt(e.target.value) || 0)}
          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          min="1"
        />
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Rate Limit (requests per minute)
        </label>
        <input
          type="number"
          value={formData.rate_limit_per_minute}
          onChange={(e) =>
            handleInputChange('rate_limit_per_minute', parseInt(e.target.value) || 0)
          }
          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          min="1"
        />
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic':
        return renderBasicTab();
      case 'branding':
        return renderBrandingTab();
      case 'layout':
        return renderLayoutTab();
      case 'appearance':
        return renderAppearanceTab();
      case 'messaging':
        return renderMessagingTab();
      case 'behavior':
        return renderBehaviorTab();
      case 'security':
        return renderSecurityTab();
      default:
        return null;
    }
  };

  return (
    <div className="w-full max-w-[1800px] mx-auto">
      {/* View Mode Toggle */}
      <div className="flex items-center justify-end gap-2 mb-4">
        <button
          type="button"
          onClick={() => setViewMode('form')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            viewMode === 'form'
              ? 'bg-blue-600 text-white'
              : 'bg-white text-gray-600 border border-gray-200 hover:border-blue-300'
          }`}
        >
          <Type className="w-4 h-4" />
          Edit Configuration
        </button>
        <button
          type="button"
          onClick={() => setViewMode('preview')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            viewMode === 'preview'
              ? 'bg-blue-600 text-white'
              : 'bg-white text-gray-600 border border-gray-200 hover:border-blue-300'
          }`}
        >
          <Eye className="w-4 h-4" />
          View Preview
        </button>
      </div>

      {/* Content */}
      <div>
        {/* Form Panel */}
        {viewMode === 'form' && (
          <div className="w-full">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Tab Navigation */}
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="flex border-b overflow-x-auto">
                  {TABS.map((tab) => {
                    const IconComponent = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        type="button"
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap transition-all border-b-2 -mb-px ${
                          activeTab === tab.id
                            ? 'text-blue-600 border-blue-600 bg-blue-50/50'
                            : 'text-gray-500 border-transparent hover:text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <IconComponent className="w-4 h-4" />
                        {tab.label}
                      </button>
                    );
                  })}
                </div>

                {/* Tab Content */}
                <div className="p-6">{renderTabContent()}</div>
              </div>

              {/* Form Actions */}
              <div className="flex items-center justify-end gap-3 pt-6 border-t">
                <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Saving...' : submitLabel}
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Preview Panel */}
        {viewMode === 'preview' && (
          <div className="w-full max-w-[1400px] mx-auto">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-3 border-b bg-gray-50">
                <Eye className="w-4 h-4 text-blue-600" />
                <h2 className="text-sm font-semibold">Live Preview</h2>
              </div>

              {/* Preview Container - Simulated Website */}
              <div className="relative" style={{ height: '900px' }}>
                {/* Fake Browser Chrome */}
                <div className="bg-gray-100 border-b border-gray-200 px-3 py-2 flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-400"></div>
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-400"></div>
                    <div className="w-2.5 h-2.5 rounded-full bg-green-400"></div>
                  </div>
                  <div className="flex-1 mx-3">
                    <div className="bg-white rounded px-2 py-1 text-xs text-gray-500 border">
                      https://your-website.com
                    </div>
                  </div>
                </div>

                {/* Real Widget Preview using iframe with widget.js */}
                <div className="bg-white" style={{ height: 'calc(100% - 40px)' }}>
                  <iframe
                    ref={previewRef}
                    key={previewKey}
                    srcDoc={generatePreviewHtml()}
                    className="w-full h-full border-0"
                    title="Widget Preview"
                    sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
